import React, { useEffect, useState } from 'react';
import { Button, Space, Table } from 'antd';
import type { TableProps } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/configureStore';
import { set_isDisplayed } from 'store/ducks/Reportings/reportingSlice';
import { reportingOp } from 'store/ducks/Reportings';
import { DownloadOutlined } from '@ant-design/icons';
import { exportToExcel } from 'utils/hooks/excelUtils99';
import {
  BProps,
  RecordType99,
  labelToCodeMap,
  labelToKeyMap,
} from 'store/ducks/Reportings/types';
import * as XLSX from 'xlsx';

const B9901: React.FC<BProps> = ({ entityName }) => {
  const dispatch = useDispatch();
  const [bordered, setBordered] = React.useState(true);
  const [empty, setEmpty] = React.useState(false);
  const [count, setCount] = React.useState(10000);
  const [dataReports, setDataReports] = useState<any>({});
  const tblRef: Parameters<typeof Table>[0]['ref'] = React.useRef(null);
  const { date, is_displayed } = useSelector(
    (state: RootState) => state.reportings,
  );

  const generateColumnData = (dataReports: any) => {
    const columnRowCounts: Record<string, number> = {};

    for (const label in labelToKeyMap) {
      const key = labelToKeyMap[label];
      const data = dataReports[key];
      columnRowCounts[label] = Array.isArray(data) ? data.length : 0;
    }

    return Object.keys(columnRowCounts).flatMap((label) => {
      const count = columnRowCounts[label];
      const code = labelToCodeMap[label];
      return Array(count)
        .fill(null)
        .map(() => ({
          code,
          name: label,
        }));
    });
  };

  const getData = (length: number, dataReports: any) => {
    const columnData = generateColumnData(dataReports);
    const columnNames = Object.keys(labelToKeyMap);
    const rows: RecordType99[] = [];
    let index = 0;

    columnNames.forEach((columnName) => {
      const rowCount = dataReports[labelToKeyMap[columnName]]?.length || 0;
      let dataKey: keyof typeof dataReports;

      switch (columnName) {
        case 'Type of contractual arrangements':
          dataKey = 'type_of_contractual_arrangement';
          break;
        case 'Impact of discontinuing the function':
          dataKey = 'impact_of_discontinuing_function';
          break;
        case 'Impact of discontinuing the ICT services':
          dataKey = 'impact_of_discontinuing_ict_services';
          break;
        case 'Possibility of reintegration of the contracted ICT service':
          dataKey = 'possibility_of_reintegration';
          break;
        case 'Sensitiveness of the data stored by the ICT third-party service provider':
          dataKey = 'sensitiveness_of_data_stored_by_ict_tpsp';
          break;
        case 'Substitutability of the ICT third-party service provider':
          dataKey = 'substitutability_of_ict_provider';
          break;
        default:
          return;
      }

      const data = dataReports[dataKey];

      if (!data || data.length < rowCount) {
        return;
      }

      let previousCode = '';
      let rowspan = 1;

      for (let i = 0; i < rowCount; i++) {
        const column = columnData[index % columnData.length];
        const currentCode = column.code;

        if (currentCode === previousCode) {
          rowspan = 0;
        } else {
          rowspan = rowCount;
        }

        rows.push({
          id: index++,
          option: '1',
          dataTxt: data[i]?.int_options_def ?? '',
          dataDesc: data[i]?.description ?? '',
          dataPoint: data[i]?.data_point ?? '',
          rowSpan: rowspan,
        });

        previousCode = currentCode;
      }
    });

    return rows;
  };

  const fetchData = async () => {
    dispatch(set_isDisplayed(false));
    if (!entityName) {
      setDataReports({});
      return;
    }

    const entityIds: string[] = entityName
      ? entityName.split(',').map((id) => id.trim())
      : [];

    const params = {
      entityId: entityIds,
      date,
    };
    try {
      const fetchedData = await reportingOp.fetchB9901(params);
      const mergedData = Object.values(fetchedData.entities).reduce(
        (acc: any, entity: any) => {
          const filtered = entity.filtered_data || {};
          for (const [key, value] of Object.entries(filtered)) {
            if (!acc[key]) acc[key] = [];
            acc[key] = acc[key].concat(value);
          }
          return acc;
        },
        {},
      );

      setDataReports(mergedData);
    } catch (error) {
      console.log('Error fetching data:', error);
      setDataReports({});
    }
  };

  useEffect(() => {
    if (entityName && entityName.trim().length > 0) {
      fetchData();
    }
  }, [entityName]);

  const data = React.useMemo<RecordType99[]>(
    () => getData(count, dataReports),
    [count, dataReports],
  );

  const handleExport = () => {
    const newSheetData = exportToExcel(
      dataReports,
      // generateColumnData(dataReports),
    );

    const wb = XLSX.utils.book_new();
    const sheetName = 'b_99.01';
    XLSX.utils.book_append_sheet(wb, newSheetData, sheetName);
    XLSX.writeFile(wb, 'ExportedData.xlsx');
  };

  const fixedColumns: TableProps<RecordType99>['columns'] = [
    {
      title: '',
      children: [
        {
          title: '',
          dataIndex: 'data_point',
          render: (_, record) => record?.dataPoint ?? '',
        },
      ],
    },
    {
      title: 'b_99.01.C0010',
      children: [
        {
          title: 'Column Code',
          width: 150,
          render: (_, record) => {
            const column = generateColumnData(dataReports)[record.id] || {};
            return column.code || '';
          },
          onCell: (record) => ({
            rowSpan: record.rowSpan,
          }),
        },
      ],
    },
    {
      title: 'b_99.01.C0020',
      children: [
        {
          title: 'Column Name',
          width: 150,
          render: (_, record) => {
            const column = generateColumnData(dataReports)[record.id] || {};
            return column.name || '';
          },
          onCell: (record) => ({
            rowSpan: record.rowSpan,
          }),
        },
      ],
    },
    {
      title: 'b_99.01.C0030',
      children: [
        {
          title: 'Options',
          dataIndex: 'int_options_def',
          render: (_, record) => record?.dataDesc ?? '',
        },
      ],
    },
    {
      title: 'b_99.01.C0040',
      children: [
        {
          title: 'Description/Internal definition of the option',
          dataIndex: 'int_options_def',
          render: (_, record) => record?.dataTxt ?? '',
        },
      ],
    },
  ];

  return (
    <div>
      <Space direction="vertical" style={{ width: '100%', textAlign: 'end' }}>
        <Table<RecordType99>
          bordered={bordered}
          columns={fixedColumns}
          rowKey="id"
          dataSource={empty ? [] : data}
          pagination={false}
          ref={tblRef}
          scroll={{ x: 1000, y: 430 }}
        />
        {entityName && (
          <Button key="submit" type="primary" onClick={handleExport}>
            <DownloadOutlined />
            Download
          </Button>
        )}
      </Space>
    </div>
  );
};

export default B9901;
